import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Search, Navigation, Zap, Dumbbell, TreePine, Map as MapIcon, Loader, ExternalLink, Compass } from 'lucide-react';
import { GoogleGenAI } from "@google/genai";
import { User, SupportedLanguage } from './types';

export const ActiveMapsView = ({ user, lang }: { user: User, lang: SupportedLanguage }) => {
  const { t } = useTranslation();
  const [queryInput, setQueryInput] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [location, setLocation] = useState<{lat: number, lng: number} | null>(null);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (pos) => setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => console.warn("Location access denied")
    );
  }, []);

  const searchSpots = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!queryInput.trim()) return;

    setLoading(true);
    try {
      const apiKey = import.meta.env.VITE_GEMINI_API_KEY || import.meta.env.API_KEY;
      if (!apiKey) {
        alert(t('map.api_key_missing'));
        setLoading(false);
        return;
      }
      
      const ai = new GoogleGenAI({ apiKey });
      const response = await ai.models.generateContent({
        model: "gemini-2.5-flash",
        contents: `Find best ${queryInput} nearby me. Suggest places for fitness, outdoor workout or healthy food. Response in ${lang}.`,
        config: {
          tools: [{ googleMaps: {} }],
          toolConfig: {
            retrievalConfig: {
              latLng: location ? { latitude: location.lat, longitude: location.lng } : undefined
            }
          }
        }
      });

      const chunks = response.candidates?.[0]?.groundingMetadata?.groundingChunks || [];
      setResults(chunks.filter((c: any) => c.maps));
    } catch (err: any) {
      console.error('[MAP] Search error:', err);
      alert(t('map.search_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 pb-32 space-y-8 max-w-5xl mx-auto min-h-screen">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black flex items-center gap-3 uppercase italic tracking-tighter">
            <Compass className="text-emerald-400" size={32} /> {t('map.title')}
          </h1>
          <p className="text-slate-500 text-sm font-medium">{t('map.subtitle')}</p>
        </div>
      </div>

      {/* Search Bar */}
      <form onSubmit={searchSpots} className="relative group">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-500 group-focus-within:text-emerald-500 transition-colors">
          <Search size={22}/>
        </div>
        <input 
          type="text"
          value={queryInput}
          onChange={e => setQueryInput(e.target.value)}
          placeholder={t('map.search_placeholder')}
          className="w-full bg-slate-900 border border-slate-800 rounded-[1.5rem] py-5 pl-12 pr-32 outline-none focus:border-emerald-500 transition-all font-bold shadow-xl"
        />
        <button 
          type="submit"
          disabled={loading}
          className="absolute right-2 top-2 bottom-2 bg-emerald-500 hover:bg-emerald-600 text-white px-6 rounded-xl font-black text-xs uppercase tracking-widest transition-all shadow-lg shadow-emerald-500/20"
        >
          {loading ? <Loader className="animate-spin" /> : t('map.search_button')}
        </button>
      </form>

      {/* Results Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {results.length === 0 && !loading && (
          <div className="col-span-full py-20 text-center space-y-4">
             <div className="w-20 h-20 bg-slate-900 border border-slate-800 rounded-full flex items-center justify-center mx-auto opacity-20">
                <MapIcon size={40}/>
             </div>
             <p className="text-slate-500 font-bold italic">{t('map.no_results')}</p>
          </div>
        )}

        {results.map((res, i) => (
          <div key={i} className="bg-slate-900 border border-slate-800 p-6 rounded-[2rem] hover:border-emerald-500/30 transition-all group animate-fadeIn">
            <div className="flex justify-between items-start mb-4">
              <div className="p-3 bg-emerald-500/10 text-emerald-500 rounded-2xl group-hover:scale-110 transition-transform">
                <Navigation size={24}/>
              </div>
              <div className="text-[10px] font-black text-emerald-500 uppercase tracking-widest bg-emerald-500/5 px-3 py-1 rounded-full border border-emerald-500/20">{t('map.verified_spot')}</div>
            </div>
            <h3 className="text-lg font-black mb-1 line-clamp-1">{res.maps.title || t('map.default_location')}</h3>
            <p className="text-xs text-slate-500 mb-6 line-clamp-2">{t('map.location_source')}</p>
            <a 
              href={res.maps.uri} 
              target="_blank" 
              className="w-full flex items-center justify-center gap-2 py-3 bg-slate-800 hover:bg-emerald-500 text-white rounded-xl text-xs font-black transition-all"
            >
              {t('map.view_on_maps')} <ExternalLink size={14}/>
            </a>
          </div>
        ))}

        {loading && [1,2,3].map(i => (
          <div key={i} className="h-64 bg-slate-900 border border-slate-800 rounded-[2rem] animate-pulse"></div>
        ))}
      </div>
    </div>
  );
};